import { useCallback, useEffect, useState } from 'react';

import Card from '@mui/material/Card';
import Table from '@mui/material/Table';
import Container from '@mui/material/Container';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';

import { useAppDispatcher, useAppSelector } from 'src/redux/store';
import { getPackagingItemUsageReport } from 'src/redux/slices/packaging-item-usage';

import Scrollbar from 'src/components/scrollbar';
import { useSettingsContext } from 'src/components/settings';
import CustomBreadcrumbs from 'src/components/custom-breadcrumbs';
import TableErrorData from 'src/components/table/table-error-data';
import {
  TableHeadCustom,
  TableNoData,
  TablePaginationCustom,
  TableSkeleton,
  useTable,
} from 'src/components/table';

import {
  IPackagingItemUsageListItem,
  IPackagingItemUsageTableFilters,
  IPackagingItemUsageTableFilterValue,
  IPackagingItemUsageTableSort,
} from 'src/types/packaging-item-usage';

import PackagingItemUsageTableRow from '../packaging-item-usage-table-row';
import PackagingItemUsageTableToolbar from '../packaging-item-usage-table-toolbar';

const TABLE_HEAD = [
  { id: 'itemName', label: 'Item Name', align: 'left' },
  { id: 'dispatched', label: 'Dispatched', align: 'right' },
  { id: 'returned', label: 'Returned', align: 'right' },
  { id: 'damaged', label: 'Damaged', align: 'right' },
  { id: 'usage', label: 'Usage', align: 'right' },
];

const today = () => {
  const date = new Date();
  date.setHours(0, 0, 0, 0);
  return date;
};

const defaultFilters: IPackagingItemUsageTableFilters = {
  generalSearch: '',
  date: today(),
};

const defaultSortBy: IPackagingItemUsageTableSort = {
  sortBy: 'itemName',
  order: 'asc',
};

export default function PackagingItemUsageView() {
  const { status, usageReport, error } = useAppSelector((state) => state.packagingItemUsage);

  const table = useTable({
    defaultRowsPerPage: 25,
  });

  const dispatch = useAppDispatcher();

  const { page, rowsPerPage } = table;

  const settings = useSettingsContext();

  const denseHeight = table.dense ? 60 : 80;

  const [filters, setFilters] = useState(defaultFilters);

  const [sort, setSort] = useState(defaultSortBy);

  const handleFilters = useCallback(
    (name: string, value: IPackagingItemUsageTableFilterValue) => {
      table.onResetPage();

      setFilters((prevState) => ({
        ...prevState,
        [name]: value,
      }));
    },
    [table]
  );

  const fetchUsageReport = useCallback(() => {
    dispatch(
      getPackagingItemUsageReport({
        date: filters.date,
        page,
        limit: rowsPerPage,
        generalSearch: filters.generalSearch,
        sort: sort.order,
        sortBy: sort.sortBy,
      })
    );
  }, [dispatch, page, rowsPerPage, sort.order, sort.sortBy, filters]);

  useEffect(() => {
    fetchUsageReport();
  }, [fetchUsageReport]);

  return (
    <Container maxWidth={settings.themeStretch ? false : 'lg'}>
      <CustomBreadcrumbs heading="Packaging Item Usage" links={[]} sx={{ mb: { xs: 3 } }} />

      <Card>
        <PackagingItemUsageTableToolbar filters={filters} onFilters={handleFilters} />

        <TableContainer sx={{ position: 'relative', overflow: 'hidden', height: '450px' }}>
          <Scrollbar>
            <Table size={table.dense ? 'small' : 'medium'} sx={{ minWidth: 760 }}>
              <TableHeadCustom
                order={sort.order}
                orderBy={sort.sortBy}
                headLabel={TABLE_HEAD}
                rowCount={usageReport.items.length}
                numSelected={table.selected.length}
                onSort={(id) => {
                  if (id === sort.sortBy) {
                    setSort((prev) => ({
                      ...prev,
                      order: prev.order === 'asc' ? 'desc' : 'asc',
                    }));
                  } else {
                    setSort({
                      sortBy: id,
                      order: 'asc',
                    });
                  }
                }}
              />

              <TableBody>
                {status === 'loading' ? (
                  [...Array(table.rowsPerPage)].map((i, index) => (
                    <TableSkeleton key={index} sx={{ height: denseHeight }} />
                  ))
                ) : (
                  <>
                    {usageReport?.items.map((row: IPackagingItemUsageListItem) => (
                      <PackagingItemUsageTableRow key={row.packagingItem._id} row={row} />
                    ))}
                  </>
                )}

                {status !== 'failure' && usageReport.count === 0 && (
                  <TableNoData
                    message="No usage found for this date"
                    notFound={usageReport.count === 0}
                  />
                )}

                {status === 'failure' && <TableErrorData notFound error={error} />}
              </TableBody>
            </Table>
          </Scrollbar>
        </TableContainer>

        <TablePaginationCustom
          count={usageReport.count}
          page={table.page}
          rowsPerPage={table.rowsPerPage}
          onPageChange={table.onChangePage}
          onRowsPerPageChange={table.onChangeRowsPerPage}
          dense={table.dense}
          onChangeDense={table.onChangeDense}
        />
      </Card>
    </Container>
  );
}
